/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { useTranslationsMap } from "common/utils/i18n"
import { Form } from "grab-ui"
import { Input } from "@duxton-web/ui"
import { Review } from "types/api/menuFeedback.type"
import { AppState } from "store/store.type"
import { FeedbackReplyState } from "types/models/feedback"
import useFEConfig from "hooks/useFEConfig"
import { replace } from "lodash"
import { renderReplyErrorMessage } from "../utils"

interface DrawerReviewReplyFormProps {
  review: Review
  form: any
  allowReply?: boolean
}

export const DrawerReviewReplyForm: React.FC<DrawerReviewReplyFormProps> = ({
  review,
  form,
  allowReply = true,
}: DrawerReviewReplyFormProps) => {
  const dispatch = useDispatch()

  const textMap = useTranslationsMap({
    placeholder: "feedback.reply.drawer.placeholder",
    helperText: "feedback.reply.drawer.helper",
    errorRequired: "menu.errors.requiredField",
    errorCharacterLimit: "menu.errors.characterLimit",
  })

  const feConfig = useFEConfig({ merchantID: review?.merchantID })
  const countLimit = feConfig.data?.reviewReplyConfig?.replyCharacterLimit || 0

  const { getFieldDecorator } = form
  const { feedback } = useSelector((state: AppState) => state)

  const handleReplyChange = (event: { target: { value: any } }) => {
    const replyText = event.target.value
    if (replyText.length === 0) {
      dispatch(actionCreator.feedback.clearReplyMessage())
    } else {
      dispatch(actionCreator.feedback.touchReplyMessage(replyText))
    }
  }

  useEffect(() => {
    if (
      feedback.replyState === FeedbackReplyState.EMPTY &&
      feedback.replyMessage
    ) {
      form.resetFields()
      dispatch(actionCreator.feedback.clearReplyMessage())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback])

  return (
    <div
      css={css({
        margin: "35px 25px",
      })}
    >
      <div>
        <Form.Item>
          {getFieldDecorator("description", {
            initialValue: review?.reviewReplies?.[0]?.description,
            rules: [
              {
                required: true,
                message: textMap.errorRequired,
              },
              countLimit > 0 && {
                max: countLimit,
                message: replace(
                  textMap.errorCharacterLimit,
                  "$limit",
                  countLimit.toString(),
                ),
              },
            ],
          })(
            <Input.TextArea
              data-testid="reply-editor-box"
              placeholder={textMap.placeholder}
              rows={4}
              onChange={handleReplyChange}
              disabled={!allowReply}
              css={
                feedback.formError
                  ? css({
                      borderColor: "#F55538",
                    })
                  : undefined
              }
            />,
          )}
          {feedback.formError && (
            <span
              css={css({
                color: "#F55538",
              })}
            >
              {renderReplyErrorMessage(feedback.formError)}
            </span>
          )}
        </Form.Item>
      </div>

      <div>
        <span
          css={css({
            fontSize: "12px",
            color: "#707070",
          })}
        >
          {textMap.helperText}
        </span>
      </div>
    </div>
  )
}
